import {
  allowsMultipleSelections,
  allowsNoSelection,
  dropPicksUnderUnpickedOptions,
  getParentOptionUidByUid,
  getSectionKey,
  isDivider,
  isFromSet,
} from '~/cynqueModules/onePerSectionSelect'
import {
  describeMissingContent,
  findMissingContent,
  isPlainKitContentOption,
} from '~/cynqueModules/kitOptionContent'
import type { IEntityMaskData, TEntityMaskItem } from '~/types/models'

/**
 * A kit's optional items as ONE FIELD PER CHOICE, built from the flat options
 * list every options source already produces (the create-time specs' loaders
 * and the kit options dialog's `buildOptionSelectItems`).
 *
 *   exclusive  a Set that takes at most one pick           -> single select
 *   multiple   a Set with doAllowMultipleSelectionsFromSet -> multiple select
 *   addOns     a plain kit's independent optional items    -> multiple select
 *
 * The fields are ordinary `select`s rendered by a stock EntityMaskForm --
 * `required` for a Set without doAllowNoSelectionFromSet, `clearable` for an
 * exclusive one that has it, its label marked "*" when it needs a pick -- so
 * each choice validates and reads like every other field in the app. Two places use them: the "Choose Options..." window
 * the Add Services flows open (OptionsChooserField.vue), and the kit options
 * dialogs, which take them as their `inputFieldsOverride` directly.
 *
 * ⚠ THE PICKS STAY ONE FLAT ARRAY everywhere else. `readPicksFromOptionFields`
 * folds the per-section values back into the array of option objects a
 * `multiple` select used to hold, so KitService's `_selectUid` match, the
 * catalogue flow's `id` match, `diffOptionSelections` and
 * `validateOnePerSection` are all untouched.
 *
 * Sections are grouped with `onePerSectionSelect`'s own predicates, so a
 * section here is exactly a section to the validator and to
 * `enforceOnePerSection`.
 */

export type TOptionSectionKind = 'exclusive' | 'multiple' | 'addOns'

/** See buildOptionSectionInputFields' `contentContext`. */
export interface IOptionContentContext {
  items: any[]
  fixedPicks: any[]
}

export interface IOptionSection {
  key: string
  kind: TOptionSectionKind
  title: string
  groupTitle: string
  // At least one pick is needed: a Set without doAllowNoSelectionFromSet.
  // Plain-kit add-ons never are.
  isRequired: boolean
  // A Set inside a Set: the outer option this section applies under. While
  // it is not picked the section is hidden, and asks for nothing.
  parentOptionUid?: string
  items: any[]
}

export interface IOptionSectionGroup {
  title: string
  sections: IOptionSection[]
}

/** Every generated section field's name starts with this. */
export const OPTION_SECTION_FIELD_PREFIX = 'optionSection__'

/** The options alone -- no divider rows. */
export function getOptionItems(items: any[] | null | undefined): any[] {
  return (items || []).filter((item) => item && !isDivider(item))
}

/**
 * The key a pick is stored and matched by. Every options source sets
 * `_selectUid`; `id` is only a fallback.
 */
export function getOptionUid(item: any): string {
  return item?._selectUid ?? item?.id
}

/** An option's own name, without the section it sits in. */
export function getOptionDescr(item: any): string {
  return (
    item?._optionDescr ||
    item?.descr ||
    item?.product?.descr ||
    item?.label ||
    ''
  ).trim()
}

function getSectionTitle(item: any): string {
  return (
    item._optionSectionDescr ||
    item.parentSupplierProductItem?.descr ||
    item.supplierProduct?.descr ||
    item.supplierProduct?.product?.descr ||
    // A catalogue-only option (Add Kit) carries its Set as a select item,
    // `{ id, label }` -- there is no `descr` to read.
    item.parentProduct?.descr ||
    item.parentProduct?.label ||
    ''
  )
}

function getSectionKind(item: any): TOptionSectionKind {
  if (!isFromSet(item)) {
    return 'addOns'
  }
  return allowsMultipleSelections(item) ? 'multiple' : 'exclusive'
}

/**
 * The sections, in the order their first option appears -- which is the
 * supplier-product's `seqNum` order, since every source collects that way.
 */
export function buildOptionSections(
  items: any[] | null | undefined
): IOptionSection[] {
  const sectionsByKey = new Map<string, IOptionSection>()
  const parentByUid = getParentOptionUidByUid(items)
  for (const item of getOptionItems(items)) {
    const key = getSectionKey(item)
    let section = sectionsByKey.get(key)
    if (!section) {
      const kind = getSectionKind(item)
      const parentOptionUid = parentByUid.get(getOptionUid(item))
      section = {
        key,
        kind,
        title: getSectionTitle(item),
        groupTitle: item._optionGroupDescr || '',
        isRequired: kind !== 'addOns' && !allowsNoSelection(item),
        ...(parentOptionUid && { parentOptionUid }),
        items: [],
      }
      sectionsByKey.set(key, section)
    }
    section.items.push(item)
  }
  return Array.from(sectionsByKey.values())
}

/**
 * Sections under their group heading (the supplier, when the Add by Product
 * flow offers several). Consecutive sections with the same group share one.
 */
export function groupOptionSections(
  sections: IOptionSection[]
): IOptionSectionGroup[] {
  const groups: IOptionSectionGroup[] = []
  for (const section of sections) {
    const last = groups[groups.length - 1]
    if (last && last.title === section.groupTitle) {
      last.sections.push(section)
    } else {
      groups.push({ title: section.groupTitle, sections: [section] })
    }
  }
  return groups
}

/**
 * A required section with only one option has nothing to decide, so it is
 * picked for the user (and its select made readonly).
 */
export function isLoneRequiredOption(section: IOptionSection): boolean {
  return section.isRequired && section.items.length === 1
}

/**
 * Does this section apply to `value`? Always, unless it is a Set inside a Set
 * whose outer option is not picked. `value` should already be free of picks
 * under unpicked options (dropPicksUnderUnpickedOptions).
 */
export function isSectionActive(
  section: IOptionSection,
  value: unknown
): boolean {
  return (
    !section.parentOptionUid ||
    toArray(value).some(
      (picked) => getOptionUid(picked) === section.parentOptionUid
    )
  )
}

function toArray(value: unknown): any[] {
  if (Array.isArray(value)) {
    return value
  }
  return value ? [value] : []
}

export function isOptionPicked(value: unknown, item: any): boolean {
  const uid = getOptionUid(item)
  return toArray(value).some((picked) => getOptionUid(picked) === uid)
}

/**
 * The flat value for a set of picked uids: options in list order, then
 * anything in the old value that is not among the options at all (kept rather
 * than silently dropped).
 */
function rebuildValue(items: any[], value: unknown, pickedUids: Set<string>) {
  const options = getOptionItems(items)
  const optionUids = new Set(options.map(getOptionUid))
  return [
    ...options.filter((item) => pickedUids.has(getOptionUid(item))),
    ...toArray(value).filter((picked) => !optionUids.has(getOptionUid(picked))),
  ]
}

/**
 * The value with every lone required option picked, or `null` when it already
 * has them all -- so the caller only writes when something actually changed.
 */
export function withLoneRequiredOptionsPicked({
  items,
  value,
}: {
  items: any[]
  value: unknown
}): any[] | null {
  // Repeated, because picking a lone option can bring a nested Set into play
  // whose own choice is lone too.
  const sections = buildOptionSections(items)
  let current = dropPicksUnderUnpickedOptions(items, toArray(value))
  let didPick = false
  for (;;) {
    const missing = sections
      .filter(
        (section) =>
          isLoneRequiredOption(section) && isSectionActive(section, current)
      )
      .map((section) => section.items[0])
      .filter((item) => !isOptionPicked(current, item))
    if (missing.length === 0) {
      break
    }
    didPick = true
    const pickedUids = new Set(current.map(getOptionUid))
    for (const item of missing) {
      pickedUids.add(getOptionUid(item))
    }
    current = rebuildValue(items, current, pickedUids)
  }
  return didPick
    ? rebuildValue(items, value, new Set(current.map(getOptionUid)))
    : null
}

/**
 * One select per section, seeded from the current picks, with a divider and a
 * subtitle heading per group when there is more than one (several suppliers).
 *
 * `value` and `start` are seeded IDENTICALLY, so an untouched field is not
 * dirty -- the kit options dialog depends on that ("absent means unchanged").
 * A lone required option is seeded as picked; NO `default` is ever set, because
 * EntityMaskForm's setDefaultValues() rewrites `value` from `default` and would
 * wipe the seeded picks.
 *
 * Each option is copied with its own short name as `label`: the sources label
 * options "Supplier | Supplier product | Item" for the flat list, and the
 * section's select already says the first two.
 *
 * `labelForSingleSection` replaces the section title when there is exactly one
 * section -- the "Swap In" / "Optional Items" dialogs scoped to one Set. A Set
 * inside a Set depending on it does not count as a second.
 */
export function buildOptionSectionInputFields({
  items,
  value,
  labelForSingleSection,
  cols = 12,
  kitHasUnconditionalContent,
  emptyKitMessage,
  contentContext,
}: {
  items: any[] | null | undefined
  value: unknown
  labelForSingleSection?: string
  // Each section select's width. Group subtitles always span the row.
  cols?: number
  /**
   * Whether the kit builds a row with nothing picked. `false` makes the first
   * top-level section refuse an all-empty choice ("Nothing would be added").
   * Defaults to what the create-time loaders stamp on the options
   * (`_kitHasUnconditionalContent`); an options dialog on an existing kit
   * passes it only for a kit that must not be emptied.
   */
  kitHasUnconditionalContent?: boolean
  /** That refusal's wording, when "Nothing would be added" is wrong. */
  emptyKitMessage?: string
  /**
   * A dialog SCOPED to one choice: the kit's whole options list, and the picks
   * outside the scope, which stand as they are. Without them the check would
   * see only this choice's picks, and refuse emptying it while another choice
   * still fills the kit.
   */
  contentContext?: IOptionContentContext
}): TEntityMaskItem[] {
  const sections = buildOptionSections(items)
  const groups = groupOptionSections(sections)
  const doShowGroupTitles =
    groups.length > 1 && groups.every((group) => !!group.title)
  const fields: TEntityMaskItem[] = []
  // Seed from picks that still apply: one under an unpicked outer option
  // would otherwise show up in a section that starts hidden.
  const activeValue = dropPicksUnderUnpickedOptions(items, toArray(value))
  // Each option's section field, so a nested section can watch its parent's.
  const fieldNameByOptionUid = new Map<string, string>()
  sections.forEach((section, index) => {
    for (const item of section.items) {
      fieldNameByOptionUid.set(
        getOptionUid(item),
        `${OPTION_SECTION_FIELD_PREFIX}${index}`
      )
    }
  })
  // `labelForSingleSection` names the ONE choice a scoped dialog offers. A Set
  // inside a Set that hangs off it ("Swap In" the managed router, then which
  // router) is part of that choice, not a second one, and keeps its own title.
  const topLevelSectionCount = sections.filter(
    (section) => !section.parentOptionUid
  ).length
  const taggedOption = getOptionItems(items).find(
    (item) => '_kitHasUnconditionalContent' in item
  )
  const kitContent =
    kitHasUnconditionalContent ??
    (taggedOption ? !!taggedOption._kitHasUnconditionalContent : undefined)
  const firstTopLevelSection = sections.find(
    (section) => !section.parentOptionUid
  )
  let sectionIndex = 0
  groups.forEach((group, groupIndex) => {
    if (doShowGroupTitles) {
      // A rule above each supplier, then its name -- kept close to the selects
      // it heads. `pb-1` is a !important utility, so it beats EntityMaskForm's
      // own `.emf-field-subtitle` padding. `mt-12` gives the rule 8px more room
      // above than a field divider's `mt-10`, setting each supplier further off
      // the one before; Vuetify emits mt-12 after mt-10, so it wins. The first
      // rule is marked so OptionsChooserField can pull it up in light mode,
      // where it follows the dialog's message rather than a block of fields.
      fields.push(
        {
          name: `optionGroupDivider__${groupIndex}`,
          label: '',
          type: 'divider',
          cols: 12,
          class:
            groupIndex === 0 ? 'mt-12 emf-first-option-group-divider' : 'mt-12',
        },
        {
          name: `optionGroup__${groupIndex}`,
          label: group.title,
          type: 'subtitle',
          cols: 12,
          class: 'pb-1',
        }
      )
    }
    for (const section of group.sections) {
      fields.push(
        buildSectionField({
          section,
          value: activeValue,
          parentFieldName: section.parentOptionUid
            ? fieldNameByOptionUid.get(section.parentOptionUid)
            : undefined,
          name: `${OPTION_SECTION_FIELD_PREFIX}${sectionIndex}`,
          cols,
          contentRule: buildContentRule({
            items,
            section,
            kitHasUnconditionalContent:
              section === firstTopLevelSection ? kitContent : undefined,
            emptyKitMessage,
            contentContext,
          }),
          label: getSectionLabel(
            section,
            labelForSingleSection &&
              !section.parentOptionUid &&
              topLevelSectionCount === 1
              ? labelForSingleSection
              : section.title
          ),
        })
      )
      sectionIndex++
    }
  })
  return fields
}

/**
 * The "would these picks build anything?" check on one section's select
 * (cynqueModules/kitOptionContent.ts): a Kit taken in THIS section with nothing
 * in it, and -- on the first top-level section only, so it is said once -- a
 * kit that would build nothing at all. Reads every section's current value,
 * since what fills a Kit is usually picked in another section.
 *
 * `undefined` when neither can happen here, so the field gets no rule.
 */
function buildContentRule({
  items,
  section,
  kitHasUnconditionalContent,
  emptyKitMessage,
  contentContext,
}: {
  items: any[] | null | undefined
  section: IOptionSection
  kitHasUnconditionalContent: boolean | undefined
  emptyKitMessage?: string
  contentContext?: IOptionContentContext
}): IEntityMaskData['validation'] | undefined {
  const sectionUids = new Set(section.items.map(getOptionUid))
  const hasKitOption = section.items.some(isPlainKitContentOption)
  if (!hasKitOption && kitHasUnconditionalContent !== false) {
    return undefined
  }
  return {
    custom: (_value, { inputFields } = {}) => {
      const fields = (inputFields || []) as TEntityMaskItem[]
      const picks = readPicksFromOptionFields({
        items,
        fields,
        changedValues: Object.fromEntries(
          fields.map((field) => [field.name, (field as IEntityMaskData).value])
        ),
      })
      const missing = findMissingContent({
        items: contentContext?.items ?? items,
        picks: [...picks, ...(contentContext?.fixedPicks ?? [])],
        kitHasUnconditionalContent,
      })
      return describeMissingContent(
        {
          isKitEmpty: missing.isKitEmpty,
          emptyKitOptions: missing.emptyKitOptions.filter((option) =>
            sectionUids.has(getOptionUid(option))
          ),
        },
        emptyKitMessage
      )
    },
  }
}

/**
 * A section's select label: its title, marked "*" when it needs a pick. That
 * is the one thing the control does not show by itself -- a single select
 * already holds one, a multiple select shows checkboxes -- and without it an
 * empty required section is only discovered when Done refuses it. A caller's
 * `labelForSingleSection` ("Swap In", "Optional Items") replaces the title and
 * is marked the same way.
 */
export function getSectionLabel(
  section: IOptionSection,
  title: string = section.title
): string {
  return section.isRequired ? `${title}*` : title
}

function buildSectionField({
  section,
  value,
  parentFieldName,
  name,
  label,
  cols,
  contentRule,
}: {
  section: IOptionSection
  value: unknown
  parentFieldName?: string
  name: string
  label: string
  cols: number
  contentRule?: IEntityMaskData['validation']
}): IEntityMaskData {
  const isExclusive = section.kind === 'exclusive'
  const isActive = isSectionActive(section, value)
  // A nested section hidden at open is not pre-picked: picking for the user
  // in a choice they cannot see would build it. Once shown it is an ordinary
  // select with one option.
  const isLone = isLoneRequiredOption(section) && isActive
  const items = section.items.map((item) => ({
    ...item,
    label: getOptionDescr(item),
  }))
  const picked = isLone
    ? items.slice(0, 1)
    : items.filter((item) => isOptionPicked(value, item))
  // An exclusive select holds one option; if the value somehow carries two,
  // the LAST wins, as in enforceOnePerSection.
  const seed = isExclusive ? picked[picked.length - 1] ?? null : picked
  return {
    name,
    label,
    type: 'select',
    doReturnObject: true,
    itemValue: '_selectUid',
    items,
    ...(!isExclusive && { multiple: true, chips: true }),
    clearable: isExclusive && !section.isRequired,
    ...((section.isRequired || contentRule) && {
      validation: {
        ...(section.isRequired && { required: true }),
        ...contentRule,
      },
    }),
    ...(isLone && { readonly: true }),
    // Not focused on open. A focused select shows no menu, so the first
    // choice merely looked active; and opening its menu instead would cover
    // the other choices before the user has seen them.
    skipAutoFocus: true,
    // A Set inside a Set: shown only while its outer option is picked in the
    // parent section's select, and emptied when hidden. EntityMaskForm re-runs
    // getStateForUnrendered after every select change, and an unrendered
    // field's `required` rule does not run.
    ...(section.parentOptionUid &&
      parentFieldName && {
        unrendered: !isActive,
        clearOnUnrender: true,
        getStateForUnrendered(inputFields: TEntityMaskItem[]) {
          const parentField = inputFields.find(
            (field) => field.name === parentFieldName
          ) as IEntityMaskData | undefined
          return (
            !parentField ||
            !!parentField.unrendered ||
            !toArray(parentField.value).some(
              (picked) => getOptionUid(picked) === section.parentOptionUid
            )
          )
        },
      }),
    cols,
    value: seed,
    start: Array.isArray(seed) ? [...seed] : seed,
  }
}

/**
 * Fold the section fields' values back into the flat array of picked options,
 * in list order.
 *
 * `changedValues` is keyed by field name. A field missing from it keeps its
 * `start` -- what EntityMaskForm's dirty-only save payload means by leaving a
 * field out. Pass every field's value to read the form as it stands.
 */
export function readPicksFromOptionFields({
  items,
  fields,
  changedValues,
}: {
  items: any[] | null | undefined
  fields: TEntityMaskItem[]
  changedValues?: Record<string, unknown> | null
}): any[] {
  const pickedUids = new Set<string>()
  for (const field of fields) {
    if (!field.name.startsWith(OPTION_SECTION_FIELD_PREFIX)) {
      continue
    }
    const fieldValue =
      changedValues && field.name in changedValues
        ? changedValues[field.name]
        : (field as IEntityMaskData).start
    for (const picked of toArray(fieldValue)) {
      pickedUids.add(getOptionUid(picked))
    }
  }
  return dropPicksUnderUnpickedOptions(
    items,
    getOptionItems(items).filter((item) => pickedUids.has(getOptionUid(item)))
  )
}
